import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useFilterContext } from './filtercontext';

export default function TextSearchFilter({
  fields = [],
  label = "Search",
  placeholder = "Search...",
  searchDelay = 300
}) {
  const { registerFilter, clearFilter } = useFilterContext();

  // State for search text
  const [searchText, setSearchText] = useState('');

  // Generate a unique field name for this filter
  const filterId = `text_search_${fields.join('_')}`;

  // Create clear handler function
  const handleClearFromContext = () => {
    setSearchText('');
  };

  // Update filter when search text changes (with debounce)
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText.trim() === '') {
        clearFilter(filterId);
      } else {
        const searchTerms = searchText.toLowerCase().split(' ').filter(term => term);

        const filterFn = item => {
          // Skip filtering if no search terms or no fields specified
          if (searchTerms.length === 0 || fields.length === 0) return true;

          // For each search term, check if any field contains it
          return searchTerms.every(term => {
            return fields.some(field => {
              const value = item[field];
              if (value === undefined || value === null) return false;

              return String(value).toLowerCase().includes(term);
            });
          });
        };

        const handleFilterDescription = () => {
          return `${label}: ${searchText}`;
        };

        registerFilter(filterId, label, filterFn, handleClearFromContext, handleFilterDescription);
      }
    }, searchDelay);

    return () => clearTimeout(handler);
  }, [searchText, filterId]);

  // Clear search
  const handleClearSearch = () => {
    setSearchText('');
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearSearch}
                edge="end"
                aria-label="clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }}
      />
    </Box>
  );
}